import React, { useState } from 'react';
import {
  Modal,
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Platform,
  useWindowDimensions,
} from 'react-native';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import { s, fs, v, borderWidth } from '../../utils/libs';
import { GTFonts } from '../../theme';
import { shouldUseWidthConstraint } from '../../utils/layoutUtils';
import { WEB_MODAL_MAX_WIDTH } from '../../theme/webLayoutWidths';

interface StockExpenseSettingModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (value: boolean) => void;
  currentValue: boolean;
}

const StockExpenseSettingModal: React.FC<StockExpenseSettingModalProps> = ({
  visible,
  onClose,
  onConfirm,
  currentValue,
}) => {
  const [selected, setSelected] = useState<boolean>(currentValue);
  const { width, height } = useWindowDimensions();
  const constrainWidth = shouldUseWidthConstraint(width, height);

  const handleConfirm = () => {
    onConfirm(selected);
    onClose();
  };

  return (
    <Modal
      animationType="slide"
      visible={visible}
      transparent
      onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View
          style={[
            styles.modalContainer,
            constrainWidth && { maxWidth: WEB_MODAL_MAX_WIDTH, width: '90%' },
          ]}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerIconRow}>
              <View style={styles.headerIconBg}>
                <Ionicons
                  name="receipt-outline"
                  size={fs(20)}
                  color="#1560bd"
                />
              </View>
              <Text style={styles.headerTitle}>Stock Expense Tracking</Text>
            </View>
            <TouchableOpacity
              onPress={onClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Ionicons name="close" size={fs(22)} color="#6B7280" />
            </TouchableOpacity>
          </View>

          {/* Description */}
          <Text style={styles.description}>
            Choose how this location handles expense records when you manually
            add products.
          </Text>

          {/* Important note */}
          {/* <View style={styles.noteBox}>
            <Ionicons
              name="information-circle"
              size={fs(16)}
              color="#92400E"
            />
            <Text style={styles.noteText}>
              This does not affect Purchase Orders sent to suppliers,
              P.O. deliveries are always recorded as stock expenses regardless of this setting.
            </Text>
          </View> */}

          {/* Radio Cards */}
          <View style={styles.cardsContainer}>
            {/* Option: Enabled */}
            <TouchableOpacity
              activeOpacity={0.7}
              onPress={() => setSelected(true)}
              style={[
                styles.radioCard,
                selected === true && styles.radioCardSelected,
              ]}>
              <View style={styles.radioRow}>
                <View
                  style={[
                    styles.radioOuter,
                    selected === true && styles.radioOuterSelected,
                  ]}>
                  {selected === true && <View style={styles.radioInner} />}
                </View>
                <View style={styles.radioContent}>
                  <View style={styles.radioLabelRow}>
                    <Text style={styles.radioLabel}>
                      Record stock expenses automatically
                    </Text>
                    <View style={styles.recommendedBadge}>
                      <Text style={styles.recommendedText}>Recommended</Text>
                    </View>
                  </View>
                  <Text style={styles.radioDescription}>
                    When you add a product or update stock quantities manually,
                    BizPlus posts a matching expense entry in your books automatically.
                  </Text>
                </View>
              </View>
            </TouchableOpacity>

            {/* Option: Disabled */}
            <TouchableOpacity
              activeOpacity={0.7}
              onPress={() => setSelected(false)}
              style={[
                styles.radioCard,
                selected === false && styles.radioCardSelected,
              ]}>
              <View style={styles.radioRow}>
                <View
                  style={[
                    styles.radioOuter,
                    selected === false && styles.radioOuterSelected,
                  ]}>
                  {selected === false && <View style={styles.radioInner} />}
                </View>
                <View style={styles.radioContent}>
                  <Text style={styles.radioLabel}>
                    No need, I'll handle expenses separately
                  </Text>
                  <Text style={styles.radioDescription}>
                    Inventory works normally but no stock expense entries are created during manual stock adjustments.
                    Use this if you track costs in a separate system or
                    spreadsheet.
                  </Text>
                </View>
              </View>
            </TouchableOpacity>
          </View>

          {/* Permanent warning */}
          <View style={styles.permanentWarning}>
            <Ionicons name="lock-closed" size={fs(14)} color="#991B1B" />
            <Text style={styles.permanentWarningText}>
              To avoid inconsistencies, this setting is permanent and cannot be changed after the business location
              is created.
            </Text>
          </View>

          {/* Confirm Button */}
          <TouchableOpacity
            onPress={handleConfirm}
            activeOpacity={0.8}
            style={styles.confirmButton}>
            <Text style={styles.confirmButtonText}>Confirm Selection</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: s(16),
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: s(16),
    padding: s(24),
    width: '100%',
    maxWidth: s(420),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: v(4) },
    shadowOpacity: 0.15,
    shadowRadius: s(12),
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: v(12),
  },
  headerIconRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: s(10),
  },
  headerIconBg: {
    width: s(36),
    height: s(36),
    borderRadius: s(10),
    backgroundColor: '#EFF6FF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontFamily: GTFonts.semibold,
    fontSize: fs(17),
    lineHeight: fs(22),
    color: '#111827',
    fontWeight: '700',
  },
  description: {
    fontFamily: GTFonts.regular,
    fontSize: fs(13.5),
    lineHeight: fs(20),
    color: '#4B5563',
    marginBottom: v(12),
  },
  noteBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FFFBEB',
    borderRadius: s(8),
    padding: s(10),
    gap: s(8),
    marginBottom: v(16),
    borderWidth: borderWidth(1),
    borderColor: '#FDE68A',
  },
  noteText: {
    flex: 1,
    fontFamily: GTFonts.regular,
    fontSize: fs(12),
    lineHeight: fs(17),
    color: '#92400E',
  },
  cardsContainer: {
    gap: v(10),
    marginBottom: v(14),
  },
  radioCard: {
    borderWidth: borderWidth(Platform.OS === 'web' ? 1 : 1.5),
    borderColor: '#E5E7EB',
    borderRadius: s(12),
    padding: s(14),
    backgroundColor: '#FAFAFA',
  },
  radioCardSelected: {
    borderColor: '#1560bd',
    backgroundColor: '#F0F7FF',
  },
  radioRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: s(12),
  },
  radioOuter: {
    width: s(20),
    height: s(20),
    borderRadius: s(10),
    borderWidth: borderWidth(2),
    borderColor: '#D1D5DB',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: v(2),
  },
  radioOuterSelected: {
    borderColor: '#1560bd',
  },
  radioInner: {
    width: s(10),
    height: s(10),
    borderRadius: s(5),
    backgroundColor: '#1560bd',
  },
  radioContent: {
    flex: 1,
  },
  radioLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: s(8),
    marginBottom: v(4),
  },
  radioLabel: {
    fontFamily: GTFonts.semibold,
    fontSize: fs(14),
    lineHeight: fs(18),
    color: '#111827',
    fontWeight: '600',
  },
  recommendedBadge: {
    backgroundColor: '#DCFCE7',
    borderRadius: s(4),
    paddingHorizontal: s(6),
    paddingVertical: v(2),
  },
  recommendedText: {
    fontFamily: GTFonts.semibold,
    fontSize: fs(10.5),
    lineHeight: fs(14),
    color: '#166534',
    fontWeight: '600',
  },
  radioDescription: {
    fontFamily: GTFonts.regular,
    fontSize: fs(12.5),
    lineHeight: fs(18),
    color: '#6B7280',
  },
  permanentWarning: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FEF2F2',
    borderRadius: s(8),
    padding: s(10),
    gap: s(8),
    marginBottom: v(16),
    borderWidth: borderWidth(1),
    borderColor: '#FECACA',
  },
  permanentWarningText: {
    flex: 1,
    fontFamily: GTFonts.semibold,
    fontSize: fs(12),
    lineHeight: fs(17),
    color: '#991B1B',
    fontWeight: '600',
  },
  confirmButton: {
    backgroundColor: '#1560bd',
    borderRadius: s(10),
    paddingVertical: v(14),
    alignItems: 'center',
  },
  confirmButtonText: {
    fontFamily: GTFonts.semibold,
    fontSize: fs(15),
    lineHeight: fs(20),
    color: '#FFFFFF',
    fontWeight: '700',
  },
});

export default StockExpenseSettingModal;
